import React, { useState, useEffect, useContext } from "react";
import { View, ScrollView, StyleSheet, Alert, RefreshControl, Platform, TouchableOpacity, Dimensions } from "react-native";
import { Appbar, Card, Title, Paragraph, Button, Chip, List, Text, Portal, Dialog, Snackbar, useTheme, Icon, IconButton, Badge, Divider, TextInput, Checkbox, Menu } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation/RootNavigator";
import { FirebaseAuthContext } from "../context/FirebaseAuthContext";
import { FirebaseTripsContext } from "../context/FirebaseTripsContext";
import { InboxEmail, ItineraryItem, TravelReservation, ReservationType } from "../types";
import { firestore } from "../services/firebase";
import { collection, query, where, orderBy, onSnapshot, doc, addDoc, deleteDoc, writeBatch, getDocs, updateDoc, serverTimestamp, limit } from "firebase/firestore";
import { format, parseISO, isValid } from "date-fns";
import Clipboard from "@react-native-clipboard/clipboard";
import { useWindowDimensions } from 'react-native';
import RenderHtml from 'react-native-render-html';
import { MaterialCommunityIcons } from '@expo/vector-icons';

const { width: screenWidth } = Dimensions.get('window');

interface EmailFolder {
  id: string;
  name: string;
  icon: string;
  isSystem: boolean;
  order: number;
  parentId?: string;
}

interface EnhancedEmail extends InboxEmail {
  folderId?: string;
  isRead?: boolean;
  isStarred?: boolean;
  associatedTripIds?: string[];
  status?: 'new' | 'processed' | 'archived';
}

const DEFAULT_FOLDERS: Omit<EmailFolder, 'id'>[] = [
  { name: 'Inbox', icon: 'inbox', isSystem: true, order: 0 },
  { name: 'Starred', icon: 'star', isSystem: true, order: 1 },
  { name: 'Processed', icon: 'check-circle', isSystem: true, order: 2 },
  { name: 'Archive', icon: 'archive', isSystem: true, order: 3 },
];

const toDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate();
  if (timestamp instanceof Date && isValid(timestamp)) return timestamp;
  if (typeof timestamp === 'string') {
    const d = parseISO(timestamp);
    if (isValid(d)) return d;
  }
  return null;
};

const reservationToActivity = (email: InboxEmail, userId: string, tripId: string): ItineraryItem | null => {
  const res = email.parsedData;
  console.log('reservationToActivity called:', {
    emailId: email.id,
    hasParsedData: !!res,
    reservationType: res?.reservation_type,
    booking: res?.booking
  });
  
  if (!res) {
    console.log('No parsedData found in email');
    return null;
  }

  const baseActivity: Partial<ItineraryItem> = {
    id: email.id,
    tripId, userId, source: 'email', status: 'confirmed',
    createdBy: userId, createdAt: new Date(), updatedAt: new Date(),
    aiConfidence: res.aiConfidence,
  };
  
  // Only add notes if they exist
  if (res.additional_notes && res.additional_notes.length > 0) {
    baseActivity.notes = res.additional_notes.join('\n');
  }

  switch (res.reservation_type) {
    case 'hotel':
      if (res.booking?.hotel) {
        const hotel = res.booking.hotel;
        baseActivity.title = `Stay at ${hotel.hotel_name}`;
        baseActivity.type = 'hotel';
        baseActivity.location = hotel.hotel_address ? `${hotel.hotel_address.street}, ${hotel.hotel_address.city}` : 'Address not found';
        baseActivity.hotelDetails = hotel;
        const checkInDate = toDate(hotel.check_in_date);
        if (checkInDate) {
          baseActivity.date = checkInDate;
          baseActivity.time = format(checkInDate, 'HH:mm');
        }
      }
      break;
    case 'flight':
      if (res.booking?.flight) {
        const flight = res.booking.flight;
        baseActivity.title = `Flight to ${flight.arrival?.city}`;
        baseActivity.type = 'flight';
        baseActivity.location = flight.departure?.airport;
        baseActivity.flightDetails = flight;
        const departureDate = toDate(flight.departure?.datetime);
        if (departureDate) {
          baseActivity.date = departureDate;
          baseActivity.time = format(departureDate, 'HH:mm');
        }
      }
      break;
    case 'train':
      if (res.booking?.train) {
        const train = res.booking.train;
        baseActivity.title = `Train to ${train.arrival?.city || train.arrivalStation}`;
        baseActivity.type = 'train';
        baseActivity.location = train.departure?.station || train.departureStation;
        baseActivity.trainDetails = train;
        baseActivity.confirmationNumber = train.bookingReference;
        
        // Extract departure time - try multiple formats
        let departureDate = null;
        if (train.departure?.datetime) {
          departureDate = toDate(train.departure.datetime);
        } else if (train.departureTime) {
          departureDate = toDate(train.departureTime);
        }
        
        if (departureDate) {
          baseActivity.date = departureDate;
          baseActivity.time = format(departureDate, 'HH:mm');
        }
        
        // Include coordinates if available
        if (train.departureCoordinates && train.departureCoordinates.latitude && train.departureCoordinates.longitude) {
          baseActivity.coordinates = train.departureCoordinates;
        }
      }
      break;
    case 'bus':
      if (res.booking?.bus) {
        const bus = res.booking.bus;
        baseActivity.title = `Bus to ${bus.arrival?.city || bus.arrivalStation}`;
        baseActivity.type = 'bus';
        baseActivity.location = bus.departure?.station || bus.departureStation;
        baseActivity.busDetails = bus;
        baseActivity.confirmationNumber = bus.bookingReference;
        
        // Extract departure time
        let departureDate = null;
        if (bus.departure?.datetime) {
          departureDate = toDate(bus.departure.datetime);
        } else if (bus.departureTime) {
          departureDate = toDate(bus.departureTime);
        }
        
        if (departureDate) {
          baseActivity.date = departureDate;
          baseActivity.time = format(departureDate, 'HH:mm');
        }
        
        // Include coordinates if available
        if (bus.departureCoordinates && bus.departureCoordinates.latitude && bus.departureCoordinates.longitude) {
          baseActivity.coordinates = bus.departureCoordinates;
        }
      }
      break;
    // Add cases for other reservation types here
  }

  // Ensure we have required fields
  if (!baseActivity.title || !baseActivity.type) {
    console.error('Activity missing required fields:', { 
      title: baseActivity.title, 
      type: baseActivity.type,
      reservationType: res.reservation_type,
      booking: res.booking
    });
    return null;
  }
  
  console.log('Activity created successfully:', {
    id: baseActivity.id,
    type: baseActivity.type,
    title: baseActivity.title
  });
  
  return baseActivity as ItineraryItem;
};

type EmailInboxNavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function EmailInboxScreen() {
  const navigation = useNavigation<EmailInboxNavigationProp>();
  const authContext = useContext(FirebaseAuthContext);
  const tripsContext = useContext(FirebaseTripsContext);
  const theme = useTheme();
  const { width } = useWindowDimensions();

  const [folders, setFolders] = useState<EmailFolder[]>([]);
  const [emails, setEmails] = useState<EnhancedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState<EnhancedEmail | null>(null);
  const [showTripDialog, setShowTripDialog] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [isAssociating, setIsAssociating] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<string | null>(null);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showEmailContent, setShowEmailContent] = useState(false);
  const [selectedEmailContent, setSelectedEmailContent] = useState<{html?: string; text?: string}>({});
  const [emailViewMode, setEmailViewMode] = useState<'html' | 'text'>('text');
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [isFolderTreeVisible, setIsFolderTreeVisible] = useState(true);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const user = authContext?.user;
  const trips = tripsContext?.trips || [];

  // Initialize default folders
  useEffect(() => {
    if (!user?.id) return;

    const initializeFolders = async () => {
      const foldersRef = collection(firestore, 'users', user.id, 'emailFolders');
      const q = query(foldersRef, orderBy('order'));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.empty) {
          // Create default folders
          const batch = writeBatch(firestore);
          DEFAULT_FOLDERS.forEach((folder) => {
            const docRef = doc(collection(firestore, 'users', user.id, 'emailFolders'));
            batch.set(docRef, {
              ...folder,
              userId: user.id,
              createdAt: serverTimestamp()
            });
          });
          batch.commit();
        } else {
          const loadedFolders = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as EmailFolder));
          setFolders(loadedFolders);
          
          // Find inbox folder and set as default if no folder is selected
          const inboxFolder = loadedFolders.find(f => f.name === 'Inbox');
          if (inboxFolder && !selectedFolder) {
            setSelectedFolder(inboxFolder.id);
          }
        }
      });

      return () => unsubscribe();
    };

    initializeFolders();
  }, [user?.id]);

  // Load emails based on selected folder
  useEffect(() => {
    if (!user?.id) return;

    // For now, let's simplify the query to just get all user emails
    // and do all filtering client-side to avoid index issues
    const q = query(
      collection(firestore, 'inboxEmails'),
      where('userId', '==', user.id),
      orderBy('receivedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log(`Email query returned ${snapshot.docs.length} emails for user ${user.id}`);
      const inboxFolder = folders.find(f => f.name === 'Inbox');
      const selectedFolderObj = folders.find(f => f.id === selectedFolder);
      let emailData = snapshot.docs.map(doc => {
        const data = doc.data();
        // Default to inbox folder if no folderId is set
        const defaultFolderId = data.folderId || inboxFolder?.id || 'inbox';
        return {
          id: doc.id,
          ...data,
          receivedAt: data.receivedAt?.toDate() || new Date(),
          folderId: defaultFolderId,
          isRead: data.isRead || false,
          isStarred: data.isStarred || false,
          associatedTripIds: data.associatedTripIds || [],
          status: data.status || 'new'
        } as EnhancedEmail;
      });

      console.log('Raw email count:', emailData.length);
      // Debug: Log all email IDs to see if we're getting all legs
      console.log('All email IDs:', emailData.map(e => e.id));
      console.log('Selected folder:', selectedFolder);
      console.log('Selected folder object:', selectedFolderObj);
      console.log('Inbox folder:', inboxFolder);
      console.log('All folders:', folders);
      
      // Log first email's folderId for debugging
      if (emailData.length > 0) {
        console.log('First email folderId:', emailData[0].folderId);
      }

      // Filter based on folder selection
      if (selectedFolder && selectedFolderObj) {
        if (selectedFolderObj.name === 'Processed') {
          emailData = emailData.filter(email => email.associatedTripIds && email.associatedTripIds.length > 0);
        } else if (selectedFolderObj.name === 'Starred') {
          emailData = emailData.filter(email => email.isStarred);
        } else if (selectedFolderObj.name === 'Inbox') {
          // For inbox, show emails that match the folder ID
          // Since we already defaulted emails without folderId to the inbox folder ID,
          // we just need to check if the email's folderId matches the selected folder
          emailData = emailData.filter(email => email.folderId === selectedFolder);
        } else {
          // For other folders, filter by folder ID
          emailData = emailData.filter(email => email.folderId === selectedFolder);
        }
      } else if (!selectedFolder && emailData.length > 0) {
        // If no folder is selected yet, show all emails (temporary state during initialization)
        console.log('No folder selected, showing all emails');
      }

      console.log('Filtered email count:', emailData.length);
      setEmails(emailData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading emails:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id, selectedFolder, folders]);

  const handleViewEmail = (email: InboxEmail) => {
    console.log("handleViewEmail called for email:", email.id);
    setSelectedEmailContent({
      html: email.content?.html,
      text: email.content?.text
    });
    // Default to text view if available, otherwise HTML
    setEmailViewMode(email.content?.text ? 'text' : 'html');
    setShowEmailContent(true);
  };

  const handleDeleteAllEmails = async () => {
    setShowDeleteAllDialog(false);
    if (!user?.id) return;

    try {
      // Only delete emails that are currently visible in the filtered view
      const batch = writeBatch(firestore);
      let deleteCount = 0;
      
      // Use the filteredEmails which already respects the current folder
      filteredEmails.forEach(email => {
        const docRef = doc(firestore, "inboxEmails", email.id);
        batch.delete(docRef);
        deleteCount++;
      });
      
      if (deleteCount > 0) {
        await batch.commit();
        setSnackbarMessage(`${deleteCount} emails deleted from current folder`);
      } else {
        setSnackbarMessage("No emails to delete in current folder");
      }
    } catch (error) {
      console.error("Error deleting all emails:", error);
      Alert.alert("Error", "Could not delete all emails.");
    }
  };

  const handleAssociateTrip = async () => {
    if (!selectedEmail || !selectedTripId || !user) return;
    setIsAssociating(true);
    try {
      const batch = writeBatch(firestore);
      
      // Check if this email is part of a multi-leg journey
      const emailIdBase = selectedEmail.id.replace(/_\d+$/, ''); // Remove _0, _1 suffix
      let relatedEmails: InboxEmail[] = [];
      
      if (selectedEmail.parsedData?.tripNumber && selectedEmail.parsedData?.totalLegs) {
        // This is part of a multi-leg journey, find all related legs
        const allEmailsQuery = query(
          collection(firestore, "inboxEmails"),
          where("userId", "==", user.id),
          where("parsedData.bookingReference", "==", selectedEmail.parsedData.booking?.train?.bookingReference || selectedEmail.parsedData.booking?.bus?.bookingReference)
        );
        const snapshot = await getDocs(allEmailsQuery);
        relatedEmails = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as InboxEmail))
          .filter(email => 
            email.parsedData?.tripNumber === selectedEmail.parsedData?.tripNumber
          )
          .sort((a, b) => (a.parsedData?.legNumber || 0) - (b.parsedData?.legNumber || 0));
        
        console.log(`Found ${relatedEmails.length} related legs for trip ${selectedEmail.parsedData.tripNumber}`);
        
        // If no related emails found, at least use the selected email
        if (relatedEmails.length === 0) {
          console.log('No related legs found, using selected email only');
          relatedEmails = [selectedEmail];
        }
      } else {
        // Single activity email
        relatedEmails = [selectedEmail];
      }
      
      console.log('Related emails to process:', relatedEmails.length);
      console.log('Selected email data:', {
        id: selectedEmail.id,
        hasParsedData: !!selectedEmail.parsedData,
        parsedData: selectedEmail.parsedData
      });
      
      // Process all related emails/legs
      let activitiesAdded = 0;
      console.log('About to process emails, relatedEmails:', relatedEmails);
      for (const email of relatedEmails) {
        console.log(`Processing email ${email.id}:`, {
          parsedData: email.parsedData,
          hasReservationType: !!email.parsedData?.reservation_type,
          hasBooking: !!email.parsedData?.booking,
          bookingKeys: email.parsedData?.booking ? Object.keys(email.parsedData.booking) : []
        });
        
        const newActivity = reservationToActivity(email, user.id, selectedTripId);
        console.log(`Creating activity from email ${email.id}:`, {
          emailType: email.parsedData?.reservation_type,
          hasActivity: !!newActivity,
          activityType: newActivity?.type,
          activityTitle: newActivity?.title,
          activityDate: newActivity?.date
        });
        
        if (newActivity) {
          // Add leg number to activity title if it's a multi-leg journey
          if (email.parsedData?.legNumber && email.parsedData?.totalLegs && email.parsedData.totalLegs > 1) {
            newActivity.title = `${newActivity.title} (Leg ${email.parsedData.legNumber}/${email.parsedData.totalLegs})`;
          }
          
          const activityRef = doc(collection(firestore, "trips", selectedTripId, "activities"), email.id);
          console.log(`Adding activity to trip ${selectedTripId}:`, newActivity);
          batch.set(activityRef, newActivity);
          activitiesAdded++;
        } else {
          console.error(`Failed to create activity from email ${email.id}`);
        }
        
        // Add email reference to trip
        const tripEmailRef = doc(collection(firestore, "trips", selectedTripId, "emails"), email.id);
        batch.set(tripEmailRef, { ...email, associatedAt: new Date(), associatedBy: user.id });
        
        // Update email status
        const inboxRef = doc(firestore, "inboxEmails", email.id);
        const processedFolder = folders.find(f => f.name === 'Processed');
        batch.update(inboxRef, {
          associatedTripIds: [...(email.associatedTripIds || []), selectedTripId],
          folderId: processedFolder?.id || email.folderId,
          status: 'processed',
          lastModified: serverTimestamp()
        });
      }
      
      console.log(`Committing batch with ${activitiesAdded} activities to trip ${selectedTripId}`);
      
      // Log details of activities being written
      const activitiesRef = collection(firestore, "trips", selectedTripId, "activities");
      console.log('Activities collection path:', `trips/${selectedTripId}/activities`);
      
      await batch.commit();
      console.log('Batch committed successfully');
      
      // Verify the write by reading back
      const verifyQuery = query(activitiesRef, limit(10));
      const verifySnapshot = await getDocs(verifyQuery);
      console.log(`Verification: Found ${verifySnapshot.size} activities in trip ${selectedTripId}`);
      verifySnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log('Verified activity:', {
          id: doc.id,
          type: data.type,
          title: data.title,
          date: data.date,
          tripId: data.tripId
        });
      });
      
      const message = activitiesAdded > 1 
        ? `Added ${activitiesAdded} activities to trip!`
        : "Email added to trip!";
      setSnackbarMessage(message);
      
      // Small delay to ensure Firestore has propagated the changes
      setTimeout(() => {
        console.log(`Navigation to trip ${selectedTripId} after adding ${activitiesAdded} activities`);
        navigation.navigate("TripDetail", { tripId: selectedTripId });
      }, 500);
    } catch (error) {
      console.error("Error associating email:", error);
      Alert.alert("Error", "Could not add email to trip.");
    } finally {
      setIsAssociating(false);
      setShowTripDialog(false);
    }
  };

  const handleDeleteEmail = (emailId: string) => {
    console.log("Delete button pressed for email:", emailId);
    if (Platform.OS === 'web') {
      setEmailToDelete(emailId);
      setShowDeleteDialog(true);
    } else {
      Alert.alert(
        "Delete Email",
        "Are you sure you want to delete this email?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Delete",
            style: "destructive",
            onPress: async () => {
              await performDelete(emailId);
            }
          }
        ]
      );
    }
  };

  const performDelete = async (emailId: string) => {
    try {
      console.log("Deleting email:", emailId);
      await deleteDoc(doc(firestore, "inboxEmails", emailId));
      setSnackbarMessage("Email deleted");
      setShowDeleteDialog(false);
      setEmailToDelete(null);
    } catch (error) {
      console.error("Error deleting email:", error);
      if (Platform.OS === 'web') {
        setSnackbarMessage("Error: Could not delete email.");
      } else {
        Alert.alert("Error", "Could not delete email.");
      }
    }
  };

  const getEmailTypeIcon = (type?: ReservationType) => {
    switch (type) {
      case 'hotel': return 'bed';
      case 'flight': return 'airplane';
      case 'train': return 'train';
      case 'bus': return 'bus';
      case 'rental': return 'car';
      default: return 'email';
    }
  };

  const handleEmailDrop = async (emailId: string, folderId: string) => {
    try {
      await updateDoc(doc(firestore, 'inboxEmails', emailId), {
        folderId,
        lastModified: serverTimestamp()
      });
      setSnackbarMessage('Email moved successfully');
    } catch (error) {
      console.error('Error moving email:', error);
      setSnackbarMessage('Failed to move email');
    }
  };

  const toggleStar = async (emailId: string, currentStarred: boolean) => {
    try {
      await updateDoc(doc(firestore, 'inboxEmails', emailId), {
        isStarred: !currentStarred
      });
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };

  const markAsRead = async (emailId: string) => {
    try {
      await updateDoc(doc(firestore, 'inboxEmails', emailId), {
        isRead: true
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const createNewFolder = async () => {
    if (!newFolderName.trim() || !user?.id) return;

    try {
      await addDoc(collection(firestore, 'users', user.id, 'emailFolders'), {
        name: newFolderName,
        icon: 'folder',
        isSystem: false,
        order: folders.length,
        userId: user.id,
        createdAt: serverTimestamp()
      });
      setNewFolderName('');
      setShowNewFolderDialog(false);
      setSnackbarMessage('Folder created successfully');
    } catch (error) {
      console.error('Error creating folder:', error);
      setSnackbarMessage('Failed to create folder');
    }
  };

  const renderFolder = (folder: EmailFolder) => {
    const emailCount = emails.filter(e => {
      if (folder.name === 'Starred') return e.isStarred;
      if (folder.name === 'Processed') return e.associatedTripIds && e.associatedTripIds.length > 0;
      if (folder.name === 'Inbox') {
        return e.folderId === folder.id || !e.folderId || e.folderId === 'inbox';
      }
      return e.folderId === folder.id;
    }).length;

    return (
      <TouchableOpacity
        key={folder.id}
        onPress={() => setSelectedFolder(folder.id)}
        style={[
          styles.folderItem,
          selectedFolder === folder.id && styles.selectedFolder
        ]}
      >
        <View style={styles.folderContent}>
          <MaterialCommunityIcons 
            name={folder.icon as any} 
            size={20} 
            color={selectedFolder === folder.id ? theme.colors.primary : theme.colors.onSurface}
          />
          <Text style={[
            styles.folderName,
            selectedFolder === folder.id && styles.selectedFolderText
          ]}>
            {folder.name}
          </Text>
          {emailCount > 0 && (
            <Badge size={20}>{emailCount}</Badge>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const filteredEmails = searchQuery
    ? emails.filter(email =>
        email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.from.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : emails;

  return (
    <View style={styles.container}>
      {/* Folder Sidebar */}
      {isFolderTreeVisible && (
        <View style={styles.folderTree}>
          <View style={styles.folderHeader}>
            <Text style={styles.folderTitle}>Folders</Text>
            <IconButton
              icon="folder-plus"
              size={20}
              onPress={() => setShowNewFolderDialog(true)}
            />
          </View>
          <Divider />
          <ScrollView>
            {folders.map(renderFolder)}
          </ScrollView>
        </View>
      )}

      {/* Toggle Button */}
      <View style={[styles.toggleButton, { left: isFolderTreeVisible ? 240 : 0 }]}>
        <IconButton
          icon={isFolderTreeVisible ? 'chevron-left' : 'chevron-right'}
          size={24}
          onPress={() => setIsFolderTreeVisible(!isFolderTreeVisible)}
        />
      </View>

      {/* Email List */}
      <View style={styles.emailList}>
        <View style={styles.toolbar}>
          <Text style={styles.emailListTitle}>
            {folders.find(f => f.id === selectedFolder)?.name || 'Emails'}
          </Text>
          <View style={styles.toolbarRight}>
            <TextInput
              mode="outlined"
              placeholder="Search..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              right={<TextInput.Icon icon="magnify" />}
              style={styles.searchBar}
              dense
            />
            <Button onPress={() => setShowDeleteAllDialog(true)} mode="text" textColor={theme.colors.error}>
              Delete All
            </Button>
          </View>
        </View>
        <Divider />
        <ScrollView>
          {filteredEmails.map((email) => {
            const reservation = email.parsedData;
            let summary = null;
            const hasTrips = email.associatedTripIds && email.associatedTripIds.length > 0;

            if (reservation) {
              switch (reservation.reservation_type) {
                case 'hotel':
                  summary = {
                    date: reservation.booking?.hotel?.check_in_date,
                    place: reservation.booking?.hotel?.hotel_name,
                  };
                  break;
                case 'flight':
                  summary = {
                    date: reservation.booking?.flight?.departure?.datetime,
                    place: `${reservation.booking?.flight?.departure?.city} to ${reservation.booking?.flight?.arrival?.city}`,
                    carrier: reservation.booking?.flight?.airline,
                    flightNumber: reservation.booking?.flight?.flight_number,
                  };
                  break;
                case 'train':
                  summary = {
                    date: reservation.booking?.train?.departure?.datetime || reservation.booking?.train?.departureTime,
                    place: `${reservation.booking?.train?.departure?.city || reservation.booking?.train?.departureStation} to ${reservation.booking?.train?.arrival?.city || reservation.booking?.train?.arrivalStation}`,
                    carrier: reservation.booking?.train?.trainOperator,
                    trainNumber: reservation.booking?.train?.trainNumber,
                    ticketClass: reservation.booking?.train?.ticketPdf?.ticketClass,
                    ticketType: reservation.booking?.train?.ticketPdf?.ticketType,
                    seat: reservation.booking?.train?.seat,
                    carriage: reservation.booking?.train?.carriage,
                  };
                  break;
                case 'bus':
                  summary = {
                    date: reservation.booking?.bus?.departure?.datetime || reservation.booking?.bus?.departureTime,
                    place: `${reservation.booking?.bus?.departure?.city || reservation.booking?.bus?.departureStation} to ${reservation.booking?.bus?.arrival?.city || reservation.booking?.bus?.arrivalStation}`,
                    carrier: reservation.booking?.bus?.operator,
                    busLine: reservation.booking?.bus?.lineNumber,
                    ticketClass: reservation.booking?.bus?.ticketPdf?.ticketClass,
                    ticketType: reservation.booking?.bus?.ticketPdf?.ticketType,
                    seat: reservation.booking?.bus?.seat,
                  };
                  break;
                default:
                  summary = null;
              }
            }

            return (
              <Card 
                key={email.id} 
                style={[
                  styles.card,
                  !email.isRead && styles.unreadEmail
                ]}
              >
                <TouchableOpacity 
                  onPress={() => {
                    markAsRead(email.id);
                    handleViewEmail(email);
                  }}
                  activeOpacity={0.7}
                >
                <Card.Content>
                  <View style={styles.emailHeader}>
                    <View onStartShouldSetResponder={() => true}>
                      <IconButton
                        icon={email.isStarred ? 'star' : 'star-outline'}
                        iconColor={email.isStarred ? '#FFA500' : undefined}
                        size={20}
                        onPress={() => toggleStar(email.id, email.isStarred || false)}
                      />
                    </View>
                    <View style={styles.emailInfo}>
                      <View style={styles.emailTitleRow}>
                        <Chip icon={getEmailTypeIcon(email.parsedData?.reservation_type)} mode="flat" compact>
                          {email.parsedData?.reservation_type || "Unknown"}
                        </Chip>
                        {email.parsedData?.isRoundTrip && (
                          <Chip 
                            mode="flat"
                            style={{ marginLeft: 4 }}
                            icon="swap-horizontal"
                            compact
                          >
                            {email.parsedData.tripDirection === 'outbound' ? 'Outbound' : 
                             email.parsedData.tripDirection === 'return' ? 'Return' : 'Round Trip'}
                          </Chip>
                        )}
                        {email.parsedData?.legNumber && email.parsedData?.totalLegs && (
                          <Chip 
                            mode="flat"
                            style={{ marginLeft: 4 }}
                            icon="map-marker-path"
                            compact
                          >
                            Leg {email.parsedData.legNumber}/{email.parsedData.totalLegs}
                          </Chip>
                        )}
                        {/* Debug: Show email ID suffix to identify legs */}
                        <Text style={{ marginLeft: 8, fontSize: 10, color: '#999' }}>
                          {email.id.split('_').pop()}
                        </Text>
                        {hasTrips && (
                          <Chip 
                            mode="flat"
                            style={styles.tripChip}
                            icon="airplane"
                            compact
                          >
                            {email.associatedTripIds.length} Trip{email.associatedTripIds.length > 1 ? 's' : ''}
                          </Chip>
                        )}
                        {email.status === 'new' && (
                          <Badge style={styles.newBadge}>NEW</Badge>
                        )}
                      </View>
                      <Title style={[!email.isRead && styles.unreadText]}>{email.subject}</Title>
                      <Paragraph>From: {email.from}</Paragraph>
                      {summary && (
                        <View style={styles.summaryContainer}>
                          {summary.date && <Text>Date: {format(toDate(summary.date)!, 'MMM dd, yyyy')}</Text>}
                          {summary.place && <Text>Place: {summary.place}</Text>}
                          {summary.carrier && <Text>Carrier: {summary.carrier}</Text>}
                          {summary.flightNumber && <Text>Flight: {summary.flightNumber}</Text>}
                          {(summary as any).trainNumber && <Text>Train: {(summary as any).trainNumber}</Text>}
                          {(summary as any).ticketClass && <Text>Class: {(summary as any).ticketClass}</Text>}
                          {(summary as any).ticketType && <Text>Ticket: {(summary as any).ticketType}</Text>}
                          {(summary as any).seat && <Text>Seat: {(summary as any).seat}</Text>}
                          {(summary as any).carriage && <Text>Carriage: {(summary as any).carriage}</Text>}
                        </View>
                      )}
                      {email.attachments && email.attachments.length > 0 && (
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                          <IconButton icon="paperclip" size={16} style={{ margin: 0 }} />
                          <Text style={{ fontSize: 12, color: '#666' }}>
                            {email.attachments.length} attachment{email.attachments.length > 1 ? 's' : ''}: 
                            {email.attachments.map(att => ` ${att.filename}`).join(',')}
                          </Text>
                        </View>
                      )}
                      {email.parsedData?.aiConfidence && <Text>AI Confidence: {Math.round(email.parsedData.aiConfidence * 100)}%</Text>}
                    </View>
                  </View>
                </Card.Content>
                </TouchableOpacity>
                <Card.Actions>
                  <View onStartShouldSetResponder={() => true}>
                    <Button onPress={() => {
                      handleViewEmail(email);
                    }}>View Email</Button>
                  </View>
                  {!hasTrips && (
                    <View onStartShouldSetResponder={() => true}>
                      <Button onPress={() => { 
                        setSelectedEmail(email); 
                        setShowTripDialog(true); 
                      }}>Add to Trip</Button>
                    </View>
                  )}
                  <View onStartShouldSetResponder={() => true}>
                    <Button onPress={() => {
                      handleDeleteEmail(email.id);
                    }} mode="text" textColor={theme.colors.error}>Delete</Button>
                  </View>
                </Card.Actions>
              </Card>
            );
          })}
        </ScrollView>
      </View>
      <Portal>
        <Dialog visible={showTripDialog} onDismiss={() => setShowTripDialog(false)}>
          <Dialog.Title>Select a Trip</Dialog.Title>
          <Dialog.Content>
            {selectedEmail?.parsedData?.totalLegs && selectedEmail.parsedData.totalLegs > 1 && (
              <Paragraph style={{ marginBottom: 12 }}>
                This journey has {selectedEmail.parsedData.totalLegs} legs. All legs will be added to the selected trip as separate activities.
              </Paragraph>
            )}
            <List.Section>
              {trips.map((trip) => (
                <List.Item key={trip.id} title={trip.name} onPress={() => setSelectedTripId(trip.id)} right={props => selectedTripId === trip.id ? <List.Icon {...props} icon="check" /> : null} />
              ))}
            </List.Section>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowTripDialog(false)}>Cancel</Button>
            <Button onPress={handleAssociateTrip} disabled={!selectedTripId || isAssociating} loading={isAssociating}>Add</Button>
          </Dialog.Actions>
        </Dialog>
        
        <Dialog visible={showDeleteDialog} onDismiss={() => setShowDeleteDialog(false)}>
          <Dialog.Title>Delete Email</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Are you sure you want to delete this email?</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onPress={() => emailToDelete && performDelete(emailToDelete)} textColor={theme.colors.error}>Delete</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={showDeleteAllDialog} onDismiss={() => setShowDeleteAllDialog(false)}>
          <Dialog.Title>Delete All Emails</Dialog.Title>
          <Dialog.Content>
            <Paragraph>
              Are you sure you want to delete all {filteredEmails.length} emails in the "{folders.find(f => f.id === selectedFolder)?.name || 'current'}" folder?
            </Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDeleteAllDialog(false)}>Cancel</Button>
            <Button onPress={handleDeleteAllEmails} textColor={theme.colors.error}>Delete All</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={showEmailContent} onDismiss={() => setShowEmailContent(false)}>
          <Dialog.Title>Email Content</Dialog.Title>
          <Dialog.Content>
            {/* Toggle buttons for HTML/Text view */}
            {selectedEmailContent.html && selectedEmailContent.text && (
              <View style={{ flexDirection: 'row', marginBottom: 10 }}>
                <Button 
                  mode={emailViewMode === 'text' ? 'contained' : 'outlined'}
                  onPress={() => setEmailViewMode('text')}
                  style={{ marginRight: 8 }}
                >
                  Plain Text
                </Button>
                <Button 
                  mode={emailViewMode === 'html' ? 'contained' : 'outlined'}
                  onPress={() => setEmailViewMode('html')}
                >
                  HTML View
                </Button>
              </View>
            )}
            
            <ScrollView style={{ maxHeight: 400 }}>
              {emailViewMode === 'html' && selectedEmailContent.html ? (
                <RenderHtml
                  contentWidth={Math.min(width - 100, 600)}
                  source={{ html: selectedEmailContent.html }}
                  tagsStyles={{
                    body: { 
                      fontSize: 14,
                      lineHeight: 20,
                      color: '#333',
                    },
                    p: { marginVertical: 5 },
                    div: { marginVertical: 2 },
                    td: { padding: 5, fontSize: 14 },
                    th: { padding: 5, fontWeight: 'bold', fontSize: 14 },
                    a: { color: '#6200ea', textDecorationLine: 'underline' },
                    h1: { fontSize: 24, marginVertical: 10 },
                    h2: { fontSize: 20, marginVertical: 8 },
                    h3: { fontSize: 18, marginVertical: 6 },
                  }}
                  ignoredDomTags={['script', 'style']}
                  systemFonts={['system-ui', '-apple-system', 'Roboto', 'sans-serif']}
                />
              ) : (
                <Text style={{ 
                  padding: 10, 
                  fontSize: 14, 
                  lineHeight: 20,
                  fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace'
                }}>
                  {selectedEmailContent.text || "No content available"}
                </Text>
              )}
            </ScrollView>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowEmailContent(false)}>Close</Button>
          </Dialog.Actions>
        </Dialog>

        {/* New Folder Dialog */}
        <Dialog visible={showNewFolderDialog} onDismiss={() => setShowNewFolderDialog(false)}>
          <Dialog.Title>Create New Folder</Dialog.Title>
          <Dialog.Content>
            <TextInput
              mode="outlined"
              label="Folder Name"
              value={newFolderName}
              onChangeText={setNewFolderName}
              autoFocus
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowNewFolderDialog(false)}>Cancel</Button>
            <Button onPress={createNewFolder}>Create</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
      <Snackbar visible={!!snackbarMessage} onDismiss={() => setSnackbarMessage("")}>{snackbarMessage}</Snackbar>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { 
    flex: 1,
    flexDirection: 'row',
  },
  folderTree: {
    width: 250,
    backgroundColor: '#f9f9f9',
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
  },
  folderHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  folderTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  folderItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    cursor: Platform.OS === 'web' ? 'pointer' : undefined,
  },
  selectedFolder: {
    backgroundColor: '#e3f2fd',
  },
  folderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  folderName: {
    flex: 1,
    fontSize: 14,
  },
  selectedFolderText: {
    fontWeight: '600',
    color: '#1976d2',
  },
  toggleButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -20,
    zIndex: 10,
    backgroundColor: '#fff',
    borderRadius: 20,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  emailList: {
    flex: 1,
    backgroundColor: '#fff',
  },
  emailListExpanded: {
    marginLeft: 0,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    minHeight: 64,
  },
  toolbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  emailListTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  searchBar: {
    width: 250,
    height: 36,
  },
  card: { 
    margin: 8,
    marginBottom: 4,
  },
  unreadEmail: {
    backgroundColor: '#f0f7ff',
  },
  emailHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  emailInfo: {
    flex: 1,
  },
  emailTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  tripChip: {
    height: 20,
  },
  newBadge: {
    backgroundColor: '#4CAF50',
  },
  unreadText: {
    fontWeight: 'bold',
  },
  summaryContainer: { marginTop: 8 },
});